/**
 * Terms of Service Page
 *
 * Displays the terms of service for RetireEZ application.
 * Accessible from Sign In page and can be viewed by any user.
 *
 * Uses LegalLayout for consistent structure (FS-016 WP-2).
 */

import { LegalLayout } from "../components/LegalLayout";

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      lastUpdated="Last Updated: February 13, 2026"
      id="terms-of-service"
    >
      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using RetireEZ (&ldquo;the Service&rdquo;), you agree
          to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you do
          not agree to these Terms, please do not use the Service.
        </p>
      </section>

      <section>
        <h2>2. Description of Service</h2>
        <p>
          RetireEZ is a Canadian retirement planning calculator that helps you
          estimate your retirement savings, income projections, and financial
          scenarios. The Service is designed for Canadian residents and uses
          Canadian financial instruments (RRSP, TFSA, CPP, OAS) and currency
          (CAD).
        </p>
      </section>

      <section>
        <h2>3. Privacy and Data Protection</h2>
        <p>
          Your financial data is encrypted and stored securely. We never sell or
          share your personal data with third parties for marketing purposes.
          For more details, please see our <a href="/privacy">Privacy Policy</a>
          .
        </p>
      </section>

      <section>
        <h2>4. No Financial Advice</h2>
        <div className="legal-callout" id="tos-disclaimer">
          <p>
            <strong>Important:</strong> RetireEZ provides general retirement
            planning tools and projections only. The Service does not provide
            financial, investment, tax, or legal advice. All calculations and
            projections are estimates based on the data you provide and should
            not be relied upon as professional advice.
          </p>
        </div>
        <p>
          You should consult with qualified financial advisors, accountants, and
          legal professionals before making any financial decisions. Past
          performance does not guarantee future results.
        </p>
      </section>

      <section>
        <h2>5. User Responsibilities</h2>
        <p>You are responsible for:</p>
        <ul>
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>Ensuring the accuracy of data you enter into the Service</li>
          <li>Complying with all applicable Canadian laws and regulations</li>
          <li>Not using the Service for any illegal or unauthorized purpose</li>
          <li>
            Not attempting to gain unauthorized access to the Service or other
            users&rsquo; data
          </li>
        </ul>
      </section>

      <section>
        <h2>6. Intellectual Property</h2>
        <p>
          The Service, including all content, features, and functionality, is
          owned by RetireEZ and is protected by Canadian and international
          copyright, trademark, and other intellectual property laws. You may
          not copy, modify, distribute, or create derivative works without our
          express written permission.
        </p>
      </section>

      <section>
        <h2>7. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by applicable law, RetireEZ and its
          operators shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages, including but not limited to loss
          of profits, data, or other intangible losses resulting from:
        </p>
        <ul>
          <li>Your use or inability to use the Service</li>
          <li>
            Any errors or inaccuracies in the Service&rsquo;s calculations or
            projections
          </li>
          <li>
            Any financial decisions made based on the Service&rsquo;s output
          </li>
          <li>Unauthorized access to or alteration of your data</li>
          <li>Any other matter relating to the Service</li>
        </ul>
      </section>

      <section>
        <h2>8. Disclaimer of Warranties</h2>
        <p>
          The Service is provided &ldquo;as is&rdquo; and &ldquo;as
          available&rdquo; without warranties of any kind, either express or
          implied. We do not warrant that the Service will be uninterrupted,
          secure, or error-free, or that any defects will be corrected.
        </p>
      </section>

      <section>
        <h2>9. Canadian Jurisdiction</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of the Province of Ontario and the federal laws of Canada
          applicable therein, without regard to conflict of law principles. Any
          disputes arising from these Terms shall be subject to the exclusive
          jurisdiction of the courts located in Ontario, Canada.
        </p>
      </section>

      <section>
        <h2>10. Changes to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will notify
          users of material changes by updating the &ldquo;Last Updated&rdquo;
          date at the top of this page. Your continued use of the Service after
          such modifications constitutes acceptance of the updated Terms.
        </p>
      </section>

      <section>
        <h2>11. Termination</h2>
        <p>
          We reserve the right to suspend or terminate your access to the
          Service at any time, without notice, for conduct that we believe
          violates these Terms or is harmful to other users, us, or third
          parties, or for any other reason in our sole discretion.
        </p>
      </section>

      <section>
        <h2>12. Contact Information</h2>
        <p>
          If you have questions about these Terms, please contact us at{" "}
          <a href="mailto:support@retire-ez.ca">support@retire-ez.ca</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
